import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Link, useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { AppBar, Box, Button, Menu, MenuItem, Tab, Tabs, Toolbar, Typography } from "@mui/material";
import Feeds from "../../pages/Feeds";
import Scheduling from "../../pages/Scheduling";
import AdminPanel from "../../pages/AdminPanel";
import ContentConfigPanel from "../../pages/ContentConfigPanel";
import AdvertiserAdminPanel from "../../pages/AdvertiserAdminPanel";
import FeaturesPage from "../../pages/FeaturesPage";
import LanguageSwitcher from "../Shared/LanguageSwitcher";
import { useTranslation } from "react-i18next";

const ADMIN_PATHS = ["/admin", "/advertiser-admin", "/content-admin"];

const MainTabs: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);

  const adminSubTabs = useMemo(() => [
    { label: t("common.admin"), path: "/admin" },
    { label: t("common.advertiserAdmin"), path: "/advertiser-admin" },
    { label: t("common.contentAdmin"), path: "/content-admin" },
  ], [t]);

  const tabConfig = useMemo(() => [
    { label: t("common.feeds"), path: "/feeds" },
    { label: t("common.scheduling"), path: "/scheduling" },
    ...(user?.role === "admin" ? [
      { label: t("common.adminFunctions"), path: "/admin-funktionen", isAdminGroup: true }
    ] : []),
  ], [user?.role, t]);

  const activeTabValue = useMemo(() => {
    if (user?.role === "admin" && ADMIN_PATHS.some((p) => location.pathname.startsWith(p))) {
      return "/admin-funktionen";
    }
    return tabConfig.find((tab) => !("isAdminGroup" in tab && tab.isAdminGroup) && location.pathname.startsWith(tab.path))?.path ?? "/feeds";
  }, [location.pathname, tabConfig, user?.role]);

  React.useEffect(() => {
    if (location.pathname === "/") {
      navigate("/feeds", { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    const tab = tabConfig.find((tc) => tc.path === newValue);
    if (tab && "isAdminGroup" in tab && tab.isAdminGroup) {
      return;
    }
    navigate(newValue);
  };

  const handleAdminTabClick = (e: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchor(e.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  const handleAdminMenuItem = (path: string) => {
    navigate(path);
    setAdminMenuAnchor(null);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <AppBar position="sticky" sx={{ top: 0, zIndex: 1100, flexShrink: 0 }}>
        <Toolbar sx={{ 
          justifyContent: "space-between", 
          alignItems: "center", 
          minHeight: "48px !important",
          paddingBottom: "3px !important",
          flexWrap: "wrap",
          gap: 1,
          position: "relative",
        }}>
          {/* Zusatzinformationen rechts - immer oben rechts */}
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            ml: "auto",
            flexShrink: 0,
            order: { xs: -1, md: 1 },
            gap: 1.5,
            flexWrap: "nowrap",
            justifyContent: "flex-end",
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mr: 0, 
                color: "#000000",
                fontSize: "0.875rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: { xs: "150px", sm: "200px", md: "250px" },
              }}
            >
              {user?.customerName} ({user?.username})
            </Typography>
            {false && user?.role === "admin" && (
              <Button
                component={Link}
                to="/features"
                sx={{ 
                  color: "#000000",
                  fontSize: "0.875rem",
                  padding: "6px 16px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                Features
              </Button>
            )}
            <Box sx={{ flexShrink: 0 }}>
              <LanguageSwitcher />
            </Box>
            <Button 
              onClick={logout}
              sx={{ 
                color: "#000000",
                fontSize: "0.875rem",
                padding: "6px 16px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              {t("common.logout")}
            </Button>
          </Box>
          {/* Tabs - mittig, können in nächste Zeile wandern */}
          <Box sx={{ 
            position: { xs: "relative", md: "absolute" },
            left: { xs: "auto", md: "50%" },
            transform: { xs: "none", md: "translateX(-50%)" },
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", md: "auto" },
            justifyContent: "center",
            order: 0,
            flex: { xs: "1 1 100%", md: "0 0 auto" },
            minWidth: 0,
            maxWidth: { xs: "100%", md: "calc(100% - 450px)" },
          }}>
            <Tabs
              value={activeTabValue}
              onChange={handleTabChange}
              sx={{
                borderBottom: "none",
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  bottom: 0,
                },
              }}
            >
              {tabConfig.map((tab) => (
                <Tab
                  key={tab.path}
                  label={tab.label}
                  value={tab.path}
                  onClick={"isAdminGroup" in tab && tab.isAdminGroup ? handleAdminTabClick : undefined}
                  sx={{
                    color: "#000000",
                    "&.Mui-selected": {
                      color: "#000000",
                      fontWeight: "bold",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.1)",
                    },
                  }}
                />
              ))}
            </Tabs>
            <Menu
              anchorEl={adminMenuAnchor}
              open={Boolean(adminMenuAnchor)}
              onClose={handleAdminMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {adminSubTabs.map((sub) => (
                <MenuItem key={sub.path} onClick={() => handleAdminMenuItem(sub.path)}>
                  {sub.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          height: "calc(100vh - 80px)",
          overflowY: "auto",
          p: 2,
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "grey.200", borderRadius: 1 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "grey.400",
            borderRadius: 4,
            "&:hover": { backgroundColor: "grey.500" },
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.08)",
        }}
      >
        <Routes>
          <Route path="/feeds" element={<Feeds />} />
          <Route path="/scheduling" element={<Scheduling />} />
          <Route path="/features" element={<FeaturesPage />} />
          {user?.role === "admin" && (
            <>
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/content-admin" element={<ContentConfigPanel />} />
              <Route path="/advertiser-admin" element={<AdvertiserAdminPanel />} />
            </>
          )}
          <Route path="*" element={<Feeds />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default MainTabs;